import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { IoEyeOutline, IoEyeOffOutline } from 'react-icons/io5';
import { MdFilterAlt, MdOutlineCheckBox, MdOutlineCheckBoxOutlineBlank, MdOutlineEdit } from 'react-icons/md';
import Header from 'components/Header';
import Tabs from 'components/Tabs';
import TextInput from 'components/TextInput';
import StudentLectures from './StudentLectures';
import PersonalizedPlan from './PersonalizedPlan';
import Button from 'components/Button';
import Dropdown from 'components/Dropdown';
import Popup from 'components/Popup';
import Table from 'components/Table';
import Loader from 'components/Loader';
import axios from 'config/axios';
import { useAuthContext } from 'context/AuthContext';
import useOutsideClickHandler from 'hooks/useOutsideClickHandler';
import TestConfigPopup from 'components/TestConfigPopup';
import { buildInitialConfig, timeConstraintLabel } from 'global/testConfig';
import Spinner from 'assets/gif/dots.gif';

import './index.css';
import Dropdown3 from 'components/Dropdown3';

// From the 2nd re-assign on, assigning the test again costs a credit.
const REASSIGN_CREDIT_NOTICE = 'Additional credit will be charged from 2nd re-assign.';
const ARCHIVE_TAB = 'Archive';

const TABS = ['Tests', 'Assignments', 'Vocab', 'Profile'];
const TABS_LECTURES = ['Tests', 'Assignments', 'Vocab', 'Lectures', 'Profile'];
const TEST_TYPE = {'All': 'All', 'SAT': 'Digital SAT', 'PSAT': 'Digital PSAT', 'ACT': 'Digital ACT', 'SHSAT': 'Digital SHSAT', 'TSIA2': 'TSIA2', 'BCA': 'BCA'};
const TEST_TYPE_PAPER = {'All': 'All', 'SAT': 'Digital SAT', 'PSAT': 'Digital PSAT', 'ACT': 'Digital ACT', 'SHSAT': 'Digital SHSAT', 'TSIA2': 'TSIA2', 'BCA': 'BCA', 'Paper ACT': 'Paper ACT'};
const SUBJECTS = {
    'Reading and Writing': 'RW',
    'Math': 'Math',
    'ACT Reading': 'R',
    'ACT Mathematics': 'M',
    'ACT English': 'E',
    'ACT Science': 'S',
    'English Language Arts': 'ELA',
    'Mathematics': 'Math',
    'TSIA2 ELAR': 'ELAR',
    'TSIA2 Mathematics': 'Math',
}
const GENDERS = [
    'Male',
    'Female',
    'Prefer not to say'
];
const CLASS_YEARS = [...Array(50)].map((_, i) => new Date().getFullYear() + 5 - i);
const ACCOMODATIONS = {
    'None': 1,
    '50% Extra': 1.5,
    '100% Extra': 2
};

function Student() {
    const [profileData, setProfileData] = useState(null);
    const [testsData, setTestsData] = useState(null);
    const [assignmentsData, setAssignmentsData] = useState(null);
    const [originalData, setOriginalData] = useState(null);
    const [tabIndex, setTabIndex] = useState(0);
    const [update, setUpdate] = useState(false);
    const [remove, setRemove] = useState(false);
    const [error, setError] = useState('');
    const [isSATActive, setIsSATActive] = useState(false);
    const [isACTActive, setIsACTActive] = useState(false);
    const [isSHSATActive, setIsSHSATActive] = useState(false);
    const [testType, setTestType] = useState(TEST_TYPE['All']);
    const [assignTests, setAssignTests] = useState(null);
    // The student's accommodation, the default timing for a new assignment.
    const [assignAccomodation, setAssignAccomodation] = useState(1);
    // The test whose config popup is open, over the Assign tests list.
    const [assignConfig, setAssignConfig] = useState<{ testId: string; mode: 'assign' | 'update' } | null>(null);
    const [assignTestsTabIndex, setAssignTestsTabIndex] = useState(0);
    const [studentName, setStudentName] = useState<string | null>(null);
    const [vocabData, setVocabData] = useState(null);
    const [vocabSubTab, setVocabSubTab] = useState(0);
    // Tests picked for bulk print, keyed by StudentTest id -- never by row
    // index, so the type filter and column sorting cannot retarget a
    // selection that was made under a different ordering.
    const [selectedTestIds, setSelectedTestIds] = useState(new Set<string>());

    const navigate = useNavigate();
    const { studentId } = useParams();
    const [searchParams] = useSearchParams();
    const { user } = useAuthContext();

    const removeStudentRef = useOutsideClickHandler(() => setRemove(false));
    const errorRef = useOutsideClickHandler(() => setError(''));
    const assignTestsRef = useOutsideClickHandler(() => setAssignTests(null));

    const testTypes = user.customizations.find(customization => customization === 'Paper Test') ? TEST_TYPE_PAPER : TEST_TYPE;
    const assignTestTypes = { ...testTypes };
    delete assignTestTypes.All;
    // Retired tests an org still holds go under their own tab, as on the
    // Tests page, instead of mixing into the type tabs.
    const assignTabs = [
        ...Object.keys(assignTestTypes),
        ...(assignTests?.some(test => test.archived) ? [ARCHIVE_TAB] : [])
    ];
    const baseTabs = user.customizations.find(customization => customization === 'Video Lectures') ? TABS_LECTURES : TABS;
    const visibleTabs = user.customizations.find(customization => customization === 'Hide Vocab')
        ? baseTabs.filter(tab => tab !== 'Vocab')
        : baseTabs;
    const tabs = [...visibleTabs, 'Personalized Plan'];

    // Display labels for the tab bar. `tabs` stays a string array (logic keys off
    // it via indexOf/=== comparisons); this parallel array only swaps the display
    // node so the Personalized Plan tab can carry a red "NEW" badge highlighting it
    // as a new feature. Keep it index-aligned with `tabs`.
    const tabLabels = tabs.map(tab =>
        tab === 'Personalized Plan'
            ? (
                <span className='student__tabLabel'>
                    Personalized Plan
                    <span className='student__tabBadge'>NEW</span>
                </span>
            )
            : tab
    );

    // Deep-link support: `?tab=plan` opens the Personalized Plan tab directly.
    // Used by the "Performance History" buttons on the individual test reports,
    // which live outside this page's tab structure and so can't just flip state.
    useEffect(() => {
        if (searchParams.get('tab') === 'plan') {
            setTabIndex(tabs.indexOf('Personalized Plan'));
        }
    }, [searchParams]);

    // Which login fields this save changes. A new password or email signs the
    // student out of the student portal (a case-only email edit doesn't); a blank
    // password field means "leave it alone".
    const changedLoginFields = () => {
        const fields = [];
        const sameEmail = (profileData.email || '').trim().toLowerCase() === (originalData?.email || '').trim().toLowerCase();
        if (!sameEmail) fields.push('email');
        if (profileData.password && profileData.password !== originalData?.password) fields.push('password');
        return fields;
    }

    const updateStudentInfo = (confirmSignOut = false) => {
        const loginFields = changedLoginFields();
        const name = profileData.firstName || 'The student';

        if (!confirmSignOut && loginFields.length > 0
            && !window.confirm(`Changing the ${loginFields.join(' and ')} will log ${name} out of the student portal. Continue?`)) {
            setProfileData(originalData);
            return;
        }

        axios.post(`tutors/students/${studentId}/update/`, {
                ...profileData,
                confirmSignOut: confirmSignOut
            })
            .then(res => {
                setOriginalData(res.data);
                setProfileData(res.data);
            })
            .catch(error => {
                if (error.response?.status === 409 && error.response.data?.code === 'student_in_test') {
                    const testName = error.response.data.testName || 'a test';
                    if (window.confirm(`${name} is taking ${testName} right now. Changing the ${loginFields.join(' and ')} will log them out, and answers in the current module that haven't been submitted may be lost. Change anyway?`)) {
                        updateStudentInfo(true);
                        return;
                    }
                    setProfileData(originalData);
                    return;
                }
                setProfileData(originalData);
                setError('Failed to update student.');
                console.log(error);
            });
    }

    const removeStudent = () => {
        axios.post(`tutors/students/${studentId}/remove/`)
            .then(res => {
                setRemove(false);
                navigate('/students', { replace: true });
            })
            .catch(error => {
                setRemove(false);
                setError('Failed to remove student.');
                console.log(error);
            });
    }

    const hideTestReview = (testId: string) => {
        axios.post('tutors/tests/hideReview/', {
                studentId: studentId,
                testId: testId
            })
            .then(res => {
                setTestsData(res.data);
            })
            .catch(error => {
                console.log(error);
            });
    }

    const showTestReview = (testId: string) => {
        axios.post('tutors/tests/showReview/', {
                studentId: studentId,
                testId: testId
            })
            .then(res => {
                setTestsData(res.data);
            })
            .catch(error => {
                console.log(error);
            });
    }

    // The report route segment for each printable test type. A type absent
    // from this map has no report page, so it cannot be printed.
    const REPORT_TYPE_SEGMENT = {
        'Digital SAT': 'digital-sat',
        'Digital PSAT': 'digital-sat',
        'BCA': 'digital-bca',
        'Digital ACT': 'digital-act',
        'Digital SHSAT': 'digital-shsat',
        'TSIA2': 'digital-tsia2',
        'Paper ACT': 'paper-act',
    };

    // Single source of truth for the rows the tests table shows. The type
    // filter used to be spelled out again for every Table prop; all of them
    // derive from this now, so a row's position cannot differ between `rows`
    // and `onSelectRow`.
    // A BCA history exists once the student has a graded BCA test.
    const isBCAActive = Boolean(testsData?.tests?.some(test => test.type === 'BCA' && test.score != null));

    const visibleTests = useMemo(() => (
        testsData
            ? testsData.tests.filter(test => (
                testTypes[testType] === 'All' || test.type === testTypes[testType]
            ))
            : []
    ), [testsData, testType]);

    // A test is printable once it has a score -- an unfinished attempt has no
    // report to print -- and only for types that have a report page.
    const isPrintable = test => Boolean(test.score) && Boolean(REPORT_TYPE_SEGMENT[test.type]);

    const printableVisibleTests = visibleTests.filter(isPrintable);
    // Deliberately over ALL tests, not just the visible ones: changing the type
    // filter must not silently drop a selection the tutor already made. Matches
    // the Students page, where the bulk-deactivate count is likewise
    // independent of the search box.
    const selectedTests = testsData
        ? testsData.tests.filter(test => selectedTestIds.has(test.testId))
        : [];

    const toggleTest = (testId: string) => {
        setSelectedTestIds(previous => {
            const next = new Set(previous);
            next.has(testId) ? next.delete(testId) : next.add(testId);
            return next;
        });
    }

    // Select-all covers the page on screen, not every printable row the filter
    // matches. Table owns pagination, so it passes the source indices of the
    // rows it is showing; they index into `visibleTests`, which the `rows` prop
    // was built from. Table has already dropped the unselectable ones.
    const toggleAllOnPage = (pageSourceIndices: number[]) => {
        const pageTests = pageSourceIndices
            .map(index => visibleTests[index])
            .filter(test => test && isPrintable(test));
        const allOnPageSelected = pageTests.length > 0
            && pageTests.every(test => selectedTestIds.has(test.testId));

        setSelectedTestIds(previous => {
            const next = new Set(previous);
            if (allOnPageSelected) {
                pageTests.forEach(test => next.delete(test.testId));
            } else {
                pageTests.forEach(test => next.add(test.testId));
            }
            return next;
        });
    }

    // Hand the selection to the print sheet in the query string as
    // `<id>:<type>` pairs, ordered as the server returned the tests (newest
    // first) rather than by the table's current sort. The sheet mounts one
    // report per pair and prints them as a single job.
    const printSelectedReports = () => {
        const tests = selectedTests
            .map(test => `${test.testId}:${REPORT_TYPE_SEGMENT[test.type]}`)
            .join(',');
        navigate(`/students/${studentId}/print-reports?tests=${tests}`);
    }

    const getTestsToAssign = () => {
        return axios.get(`tutors/students/${studentId}/assignTests/`)
            .then(res => {
                setAssignTests(res.data.tests);
                setAssignAccomodation(parseFloat(res.data.accomodation ?? 1));
            })
            .catch(error => {
                console.log(error);
                setAssignTests(null);
            });
    }

    // The Student-page row in the shape the config popup reads.
    const configTest = (test) => ({ ...test, type: test.testType });
    const configExisting = (test) => ({ ...test, testSections: test.studentTestSections ?? [] });
    const assignConfigTest = assignConfig && assignTests?.find(test => test.testId === assignConfig.testId);

    // Same endpoints as the Tests page; the route's studentId is the
    // StudentOrganization id. Rejects with the server error for the popup.
    const submitAssignConfig = (payload) => {
        const request = assignConfig.mode === 'assign'
            ? axios.post('tutors/tests/assign/', {
                ...payload,
                action: 'assign',
                studentOrganizationId: studentId,
                testId: assignConfig.testId
            })
            : axios.post('tutors/tests/configure/', {
                ...payload,
                studentOrganizationId: studentId,
                testId: assignConfig.testId
            });

        return request.then(() => {
            setAssignConfig(null);
            return getTestsToAssign();
        });
    }

    const unassignTest = (testId: string) => {
        axios.post('tutors/tests/assign/', {
            action: 'unassign',
            studentOrganizationId: studentId,
            testId: testId
        })
        .then(() => getTestsToAssign())
        .catch(error => {
            console.log(error);
        });
    }

    const hideAssignmentReview = (assignmentId: string) => {
        axios.post('tutors/assignments/hideReview/', {
            studentId: studentId,
            questionSetId: assignmentId
        })
        .then(res => {
            let newQuestionSets = [ ...assignmentsData.questionSets ];
            const questionSetIndex = newQuestionSets.findIndex(questionSet => questionSet.questionSetId === assignmentId);

            if (questionSetIndex > -1) {
                newQuestionSets[questionSetIndex].questionSetReview = false;
            }

            setAssignmentsData({
                ...assignmentsData,
                questionSets: newQuestionSets
            });
        })
        .catch(error => {
            console.log(error);
        });
    }

    const showAssignmentReview = (assignmentId: string) => {
        axios.post('tutors/assignments/showReview/', {
            studentId: studentId,
            questionSetId: assignmentId
        })
        .then(res => {
            let newQuestionSets = [ ...assignmentsData.questionSets ];
            const questionSetIndex = newQuestionSets.findIndex(questionSet => questionSet.questionSetId === assignmentId);

            if (questionSetIndex > -1) {
                newQuestionSets[questionSetIndex].questionSetReview = true;
            }

            setAssignmentsData({
                ...assignmentsData,
                questionSets: newQuestionSets
            });
        })
        .catch(error => {
            console.log(error);
        });
    }

    useEffect(() => {
        axios.get(`tutors/students/${studentId}/`)
            .then(res => {
                setStudentName(`${res.data.firstName} ${res.data.lastName}`);
            })
            .catch(() => {});
    }, [studentId]);

    useEffect(() => {
        if (tabs[tabIndex] === 'Profile') {
            axios.get(`tutors/students/${studentId}/`)
                .then(res => {
                    setOriginalData(res.data);
                    setProfileData(res.data);
                })
                .catch(error => {
                    console.log(error);
                });
        }
        else if (tabs[tabIndex] === 'Tests') {
            axios.get(`tutors/students/${studentId}/tests/`)
                .then(res => {
                    setTestsData(res.data);
                    setIsSATActive(res.data.isSATActiveHistoricalReport);
                    setIsACTActive(res.data.isACTActiveHistoricalReport );
                    setIsSHSATActive(res.data.isSHSATActiveHistoricalReport);
                })
                .catch(error => {
                    console.log(error);
                });
        }
        else if (tabs[tabIndex] === 'Assignments') {
            axios.get(`tutors/students/${studentId}/assignments/`)
                .then(res => {
                    setAssignmentsData(res.data);
                })
                .catch(error => {
                    console.log(error);
                });
        }
        else if (tabs[tabIndex] === 'Vocab') {
            axios.get(`vocab/tutor/student/${studentId}/progress/`)
                .then(res => {
                    setVocabData(res.data);
                })
                .catch(error => {
                    console.log(error);
                });
        }
    }, [tabIndex, studentId]);

    useEffect(() => {
        if (!assignTests) {
            setAssignTestsTabIndex(0);
            axios.get(`tutors/students/${studentId}/tests/`)
                .then(res => {
                    setTestsData(res.data);
                    setIsSATActive(res.data.isSATActiveHistoricalReport);
                    setIsACTActive(res.data.isACTActiveHistoricalReport );
                    setIsSHSATActive(res.data.isSHSATActiveHistoricalReport);
                })
                .catch(error => {
                    console.log(error);
                });
        }
    }, [assignTests]);

    const removePopup = (
        <Popup
            title='Remove student'
            primaryButtonText='Remove'
            primaryButtonOnClick={removeStudent}
            secondaryButtonText='Cancel'
            secondaryButtonOnClick={() => setRemove(false)}
            innerRef={removeStudentRef}
        >
            <p>
                This student will be removed from your list of students.
                <br/><br/>
                Are you sure you want to remove this student?
            </p>
        </Popup>
    );

    const failedPopup = (
        <Popup
            title='Failed'
            primaryButtonText='Close'
            primaryButtonOnClick={() => setError('')}
            innerRef={errorRef}
        >
            {error}
        </Popup>
    );

    return (
        <div className='student'>
            <Helmet>
                <title>
                    {studentName || 'Student'} - Test Center Admin
                </title>
            </Helmet>
            <Header
                title={studentName}
                prevRoutes={[{ name: 'All Students', path: '/students' }]}
            />
            <div className='student__bar'>
                <Tabs
                    tabs={tabLabels}
                    tabIndex={tabIndex}
                    onSelect={tabs.map((_tab, t) => (
                        () => {
                            if (tabIndex !== t) {
                                setTabIndex(t);
                            }
                        }
                    ))}
                />
                {tabs[tabIndex] === 'Tests' &&
                    <div className='student__buttons'>
                        {selectedTests.length > 0 &&
                            <Button
                                className='student__button'
                                onClick={printSelectedReports}
                            >
                                Print Reports ({selectedTests.length})
                            </Button>
                        }
                        <Button
                            // className='student__button'
                            onClick={getTestsToAssign}
                            secondary
                        >
                            Assign
                        </Button>
                        {(isSATActive||isACTActive||isSHSATActive||isBCAActive) &&
                            <Button className='student__button'
                                onClick={testType === 'BCA' || !(isSATActive||isACTActive||isSHSATActive)
                                    ? () => navigate(`/students/${studentId}/digital-bca/performanceHistory`)
                                    : testType === 'SAT' || testType === 'All'
                                    ? (isSATActive
                                        ? () => navigate(`/students/${studentId}/digital-sat/performanceHistory`)
                                        : isACTActive
                                        ? () => navigate(`/students/${studentId}/digital-act/performanceHistory`)
                                        : () => navigate(`/students/${studentId}/digital-shsat/performanceHistory`)
                                    )
                                    : testType === 'ACT'
                                    ? (isACTActive
                                        ? () => navigate(`/students/${studentId}/digital-act/performanceHistory`)
                                        : isSATActive
                                        ? () => navigate(`/students/${studentId}/digital-sat/performanceHistory`)
                                        : () => navigate(`/students/${studentId}/digital-shsat/performanceHistory`)
                                    )
                                    : testType === 'SHSAT'
                                    ? (isSHSATActive
                                        ? () => navigate(`/students/${studentId}/digital-shsat/performanceHistory`)
                                        : isSATActive
                                        ? () => navigate(`/students/${studentId}/digital-sat/performanceHistory`)
                                        : () => navigate(`/students/${studentId}/digital-act/performanceHistory`)
                                    )
                                    : null
                                }
                            >
                                Performance History
                            </Button>
                        }
                        <Dropdown3
                            data={Object.keys(testTypes)}
                            value={testType}
                            onSelect={value => setTestType(value)}
                            Icon={MdFilterAlt}
                        />
                    </div>
                }
            </div>
            {(profileData && tabs[tabIndex] === 'Profile')
            ?   <div className='student'>
                    {error && failedPopup}
                    {remove && removePopup}
                    <div className='student__profile'>
                        <TextInput
                            label='Student ID'
                            value={profileData.studentId}
                            disabled={!update}
                            onChange={event => setProfileData({
                                ...profileData,
                                studentId: event.target.value
                            })}
                        />
                        <div className='student__grid'>
                            <TextInput
                                label='First name'
                                value={profileData.firstName}
                                disabled={!update}
                                onChange={event => setProfileData({
                                    ...profileData,
                                    firstName: event.target.value
                                })}
                            />
                            <TextInput
                                label='Last name'
                                value={profileData.lastName}
                                disabled={!update}
                                onChange={event => setProfileData({
                                    ...profileData,
                                    lastName: event.target.value
                                })}
                            />
                            <TextInput
                                label='Email'
                                value={profileData.email}
                                disabled={!update}
                                onChange={event => setProfileData({
                                    ...profileData,
                                    email: event.target.value
                                })}
                            />
                            <TextInput
                                label='Password'
                                value={profileData.password}
                                disabled={!update}
                                onChange={event => setProfileData({
                                    ...profileData,
                                    password: event.target.value
                                })}
                            />
                            <TextInput
                                label='School'
                                value={profileData.highSchool}
                                disabled={!update}
                                onChange={event => setProfileData({
                                    ...profileData,
                                    highSchool: event.target.value
                                })}
                            />
                            <Dropdown
                                label='Class of'
                                data={CLASS_YEARS}
                                value={profileData.classOf}
                                disabled={!update}
                                onSelect={value => setProfileData({
                                    ...profileData,
                                    classOf: value
                                })}
                            />
                            <TextInput
                                label='Phone number'
                                value={profileData.phoneNumber}
                                disabled={!update}
                                onChange={event => {
                                    const value = event.target.value.replace(/[^\d]/g, '');
                                    if (value.length <= 15) {
                                        setProfileData({
                                            ...profileData,
                                            phoneNumber: value
                                        });
                                    }
                                }}
                            />
                            <Dropdown
                                label='Gender'
                                data={GENDERS}
                                value={profileData.gender}
                                disabled={!update}
                                onSelect={value => setProfileData({
                                    ...profileData,
                                    gender: value
                                })}
                            />
                            <TextInput
                                label='Date of birth'
                                value={profileData.birthDate}
                                disabled={!update}
                                onChange={event => setProfileData({
                                    ...profileData,
                                    birthDate: event.target.value
                                })}
                                type='date'
                            />
                            <Dropdown
                                label='Time Extension'
                                data={Object.keys(ACCOMODATIONS)}
                                disabled={!update}
                                onSelect={(value) => setProfileData({
                                    ...profileData,
                                    accomodation: ACCOMODATIONS[value]
                                })}
                                value={Object.keys(ACCOMODATIONS).find(key => ACCOMODATIONS[key] == profileData.accomodation)}
                            />
                        </div>
                    </div>
                    <Button
                        className='student__update'
                        secondary={update}
                        onClick={() => {
                            if (update) updateStudentInfo(false);
                            setUpdate(!update);
                        }}
                    >
                        {update ? 'Save' : 'Edit'}
                    </Button>
                    <Button
                        className='student__delete'
                        onClick={() => setRemove(true)}
                    >
                        Remove Account
                    </Button>
                </div>
            :   (testsData && tabs[tabIndex] === 'Tests')
            ?   <>
                    {/* The config popup takes the list's place while open, so
                        the list's outside-click handler can't close it. */}
                    {assignTests !== null && assignConfigTest &&
                        <TestConfigPopup
                            test={configTest(assignConfigTest)}
                            initialConfig={buildInitialConfig(
                                configTest(assignConfigTest),
                                assignConfig.mode === 'update' ? configExisting(assignConfigTest) : null,
                                assignAccomodation
                            )}
                            mode={assignConfig.mode}
                            notice={assignConfig.mode === 'assign' && assignConfigTest.attempts > 1 ? REASSIGN_CREDIT_NOTICE : undefined}
                            onSubmit={submitAssignConfig}
                            onClose={() => setAssignConfig(null)}
                        />
                    }
                    {assignTests !== null && !assignConfigTest &&
                        <Popup
                            title='Assign tests'
                            secondaryButtonText='Close'
                            secondaryButtonOnClick={() => setAssignTests(null)}
                            innerRef={assignTestsRef}
                            closeButtonOnClick={() => setAssignTests(null)}
                        >
                            <Tabs
                                tabs={assignTabs}
                                tabIndex={assignTestsTabIndex}
                                onSelect={assignTabs.map((_tab, t) => (
                                    () => setAssignTestsTabIndex(t)
                                ))}
                            />
                            {/* Fixed height with its own scroll, and no pages: switching
                                tabs neither resizes the popup nor lands on an empty page 2. */}
                            <div className='student__assignTestsScroll'>
                            <Table
                                key={assignTabs[assignTestsTabIndex]}
                                className='student__assignTests'
                                navigator={false}
                                header={[
                                    '',
                                    'Test name',
                                    'Sections',
                                    'Timing',
                                    'Attempts'
                                ]}
                                rows={assignTests.filter(test => (
                                    assignTabs[assignTestsTabIndex] === ARCHIVE_TAB
                                        ? test.archived
                                        : test.testType === assignTestTypes[assignTabs[assignTestsTabIndex]] && !test.archived
                                )).map(test => ([
                                    test.testAssigned
                                        ?   <span className='student__assignCheck'>
                                                <MdOutlineCheckBox
                                                    className={test.inProgress ? 'student__checkBox--disabled' : 'student__checkBox'}
                                                    onClick={() => !test.inProgress && unassignTest(test.testId)}
                                                />
                                                {test.inProgress
                                                ?   <img className='student__assignLoader' src={Spinner} title='In progress' />
                                                :   <MdOutlineEdit
                                                        className='student__assignEdit'
                                                        onClick={() => setAssignConfig({ testId: test.testId, mode: 'update' })}
                                                    />
                                                }
                                            </span>
                                        :   <MdOutlineCheckBoxOutlineBlank
                                                className='student__checkBox--blank'
                                                onClick={() => setAssignConfig({ testId: test.testId, mode: 'assign' })}
                                            />,
                                    test.name,
                                    test.testAssigned
                                        ? (test.studentTestSections ?? []).map(section => SUBJECTS[section.subject]).join(' + ')
                                        : null,
                                    test.testAssigned
                                        ? (test.inProgress ? 'In progress' : timeConstraintLabel(test.timeExtension))
                                        : null,
                                    test.attempts
                                ]))}
                            />
                            </div>
                        </Popup>
                    }
                    <Table
                        key = {testType}
                        className='student__tests'
                        header={[
                            'Assigned Date',
                            'Name',
                            'Section',
                            'Timing',
                            'Progress',
                            'Score',
                            'Test Date',
                            ... user.customizations.find(customization => customization === 'Hide Report') ? ['Show/Hide Report'] : [],
                            'Report'
                        ]}
                        rows={visibleTests.map(test => ([
                            new Date(test.assignedDate).toLocaleDateString(),
                            <span style={{ whiteSpace: 'nowrap' }}>{test.name}</span>,
                            test.testSection,
                            timeConstraintLabel(test.timeExtension),
                            <div className='student__progress'>
                                {new Array(test.testModuleCount).fill(null).map((_count, c) => (
                                    <div className={test.completedModuleCount > c
                                        ? 'student__progressBar--filled'
                                        : (test.inProgress && c === 0)
                                        ? 'student__progressBar--started'
                                        : 'student__progressBar'
                                    }></div>
                                ))}
                            </div>,
                            // test.inProgress ? <div className='student__progressBar'>
                            //     <span style={{ width: 
                            //         `${100*test.completedModuleCount/test.testModuleCount}%`
                            //     }}></span>
                            // </div> : null,
                            // test.type,
                            test.type === 'Digital SAT' || test.type === 'Digital PSAT'
                                ? test.score? Math.floor(test.score): '-'
                                : test.type === 'BCA'
                                // +2 / -0.5 per question: keep the half point and the sign.
                                ? (test.score != null ? Number(test.score) : '-')
                                : test.type === 'Digital ACT'
                                ? (test.compositeScore? Math.round(test.compositeScore): '-')
                                : test.type === 'Digital SHSAT'
                                // RAW score as a fraction (correct / total). Uses
                                // rawCorrect, not the scaled score (which for adaptive
                                // read as a bogus ~207% when divided by question count).
                                ? (test.rawCorrect != null && test.totalQuestionCount
                                    ? <span style={{ whiteSpace: 'nowrap' }}>{test.rawCorrect} / {test.totalQuestionCount}</span>
                                    : '-')
                                : test.type === 'TSIA2'
                                // Flat percent correct over what was served.
                                ? (test.score != null ? `${Math.round(test.score)}%` : '-')
                                : '-',
                            test.testDate ? new Date(test.testDate).toLocaleDateString() : null,
                            ... user.customizations.find(customization => customization === 'Hide Report')
                                ? [test.testReview
                                    ? <IoEyeOutline className='student__show' />
                                    : <IoEyeOffOutline className='student__hide' />
                                ] : [],
                            test.score ? 'View' : null
                        ]))}
                        onSelectCell={visibleTests.map(test => ([
                            null,
                            null,
                            null,
                            null,
                            null,
                            null,
                            null,
                            ... user.customizations.find(customization => customization === 'Hide Report')
                                ? [test.testReview
                                    ? () => hideTestReview(test.testId)
                                    : () => showTestReview(test.testId)
                                ] : [],
                            test.score && (
                                test.type === 'Digital SAT' || test.type === 'Digital PSAT'
                                    ? () => navigate(`/report/digital-sat/${test.testId}`)
                                    : test.type === 'BCA'
                                    ? () => navigate(`/report/digital-bca/${test.testId}`)
                                    : test.type === 'Digital ACT'
                                    ? () => navigate(`/report/digital-act/${test.testId}`)
                                    : test.type === 'Digital SHSAT'
                                    ? () => navigate(`/report/digital-shsat/${test.testId}`)
                                    : test.type === 'TSIA2'
                                    ? () => navigate(`/report/digital-tsia2/${test.testId}`)
                                    : test.type === 'Paper ACT'
                                    ? () => navigate(`/report/paper-act/${test.testId}`)
                                    : null
                            )
                            // test.score ? () => window.open(`${process.env.CLIENT_BASE_URL}/report/digital-sat/${test.testId}`, '_blank') : null
                        ]))}
                        selectType='checkBox'
                        onSelectRow={visibleTests.map(test => () => toggleTest(test.testId))}
                        rowsSelected={visibleTests.map(test => selectedTestIds.has(test.testId))}
                        rowsSelectable={visibleTests.map(test => isPrintable(test))}
                        onSelectAll={toggleAllOnPage}
                    />
                </>
            :   (assignmentsData && tabs[tabIndex] === 'Assignments')
            ?   <Table
                    className='student__tests'
                    header={[
                        'Assigned Date',
                        'Name',
                        'Test Type',
                        'Subject',
                        'Score',
                        'Completed Date',
                        ... user.customizations.find(customization => customization === 'Hide Report') ? ['Show/Hide Report'] : [],
                        'Report'
                    ]}
                    rows={assignmentsData.questionSets.map(assignment => ([
                        new Date(assignment.assignedDate).toLocaleDateString(),
                        assignment.name,
                        assignment.testType,
                        assignment.subject,
                        assignment.score,
                        assignment.completedDate
                            ? new Date(assignment.completedDate).toLocaleDateString()
                            : null,
                        ... user.customizations.find(customization => customization === 'Hide Report')
                            ? [assignment.questionSetReview
                                ? <IoEyeOutline className='student__show' />
                                : <IoEyeOffOutline className='student__hide' />
                            ] : [],
                        assignment.score !== null ? 'Report' : null
                    ]))}
                    onSelectCell={assignmentsData.questionSets.map(assignment => ([
                        null,
                        null,
                        null,
                        null,
                        null,
                        null,
                        ... user.customizations.find(customization => customization === 'Hide Report')
                            ? [assignment.questionSetReview
                                ? () => hideAssignmentReview(assignment.questionSetId)
                                : () => showAssignmentReview(assignment.questionSetId)
                            ] : [],
                        assignment.score !== null
                            ? () => navigate(`/report/assignment/${assignment.questionSetId}`)
                            : null
                    ]))}
                />
            :   (vocabData && tabs[tabIndex] === 'Vocab')
            ?   <>
                    <div className='student__vocabSubTabs'>
                        <button
                            className={vocabSubTab === 0 ? 'student__vocabSubTab--active' : 'student__vocabSubTab'}
                            onClick={() => setVocabSubTab(0)}
                        >
                            Flashcard Decks
                        </button>
                        <button
                            className={vocabSubTab === 1 ? 'student__vocabSubTab--active' : 'student__vocabSubTab'}
                            onClick={() => setVocabSubTab(1)}
                        >
                            Quiz Results
                        </button>
                    </div>
                    {vocabSubTab === 0
                        ? <Table
                            className='student__tests'
                            header={[
                                'Assigned Date',
                                'Subject',
                                'Status',
                                'Mastered',
                                'Learning',
                                'Not Seen',
                                'Mastery %',
                                'Completed Date'
                            ]}
                            rows={vocabData.assignments.map(assignment => ([
                                new Date(assignment.createdAt).toLocaleDateString(),
                                assignment.subject,
                                assignment.status === 'completed' ? 'Completed'
                                    : assignment.status === 'in_progress' ? 'In Progress'
                                    : 'Not Started',
                                assignment.mastered,
                                assignment.learning,
                                assignment.notSeen,
                                `${assignment.masteryPercent}%`,
                                assignment.completedDate
                                    ? new Date(assignment.completedDate).toLocaleDateString()
                                    : null
                            ]))}
                        />
                        : (() => {
                            const quizRows = vocabData.assignments.flatMap(assignment => {
                                const attempts = assignment.quizAttempts || [];
                                const countByTest = {};
                                return attempts.map(attempt => {
                                    countByTest[attempt.testNumber] = (countByTest[attempt.testNumber] || 0) + 1;
                                    const n = countByTest[attempt.testNumber];
                                    const totalForTest = attempts.filter(a => a.testNumber === attempt.testNumber).length;
                                    const label = totalForTest > 1
                                        ? `Quiz ${attempt.testNumber} Attempt ${n}`
                                        : `Quiz ${attempt.testNumber}`;
                                    return {
                                        row: [
                                            assignment.subject,
                                            label,
                                            `${attempt.score}%`,
                                            attempt.completedDate
                                                ? new Date(attempt.completedDate).toLocaleDateString()
                                                : null,
                                            'Report'
                                        ],
                                        date: attempt.completedDate,
                                        quizId: attempt.id,
                                    };
                                });
                            }).sort((a, b) => {
                                if (!a.date || !b.date) return 0;
                                return new Date(b.date).getTime() - new Date(a.date).getTime();
                            });
                            return <Table
                                className='student__tests'
                                header={['Subject', 'Quiz', 'Score', 'Completed Date', '']}
                                rows={quizRows.map(item => item.row)}
                                onSelectCell={quizRows.map(item => ([
                                    null,
                                    null,
                                    null,
                                    null,
                                    () => navigate(`/vocab/quiz/${item.quizId}`)
                                ]))}
                            />;
                        })()
                    }
                </>
            :   (tabs[tabIndex] === 'Lectures')
            ?   <StudentLectures studentId={studentId} />
            :   (tabs[tabIndex] === 'Personalized Plan')
            ?   <PersonalizedPlan studentId={studentId} />
            :   <Loader />
            }
        </div>
    );
}

export default Student;